/**
 * Gate と Board を別のアプリとして公開する(2026-10-23 発注者決定: 独立しているが連動する形)。
 * コードの置き場所(リポジトリ)は1つのまま、ビルドで Gate 用・Board 用の2つを作る(`npm run build` / `npm run build:board`)。
 *
 *  - Gate(Nexus Gate): 入力・承認・支払・請求と入金・入出金・マスタ管理(正本)
 *  - Board(Nexus Board): 見るのが中心。全社PL・勘定科目別・事業部予実・本部PL・経費管理表・CF月次推移・予算入力
 *  - ログイン・データ(Firebase)は共通。画面どうしは相手のアプリの URL へのリンクで行き来する
 *  - 相手の URL は Cloudflare の設定(VITE_GATE_URL / VITE_BOARD_URL)。**Gate に Board の URL が無い間は、Board の画面を
 *    今までどおり Gate の中でも開ける**(移行中に見られなくならないように)
 *
 * どの画面がどちらのアプリか・Board で最初に開く画面は、具体例を tests/unit/appTarget.test.ts に置く
 */

export type AppTarget = 'gate' | 'board';

/** ビルドで決まる、いま動いているアプリ(vite.config.ts の define。テストなどで未定義なら Gate) */
export const APP: AppTarget = typeof __APP__ !== 'undefined' && __APP__ === 'board' ? 'board' : 'gate';

export const APP_NAMES: Record<AppTarget, string> = { gate: 'Nexus Gate', board: 'Nexus Board' };

/** Board の画面(これ以外は Gate) */
export const BOARD_PATHS = ['/pl', '/pl/accounts', '/pl/dept', '/pl/honbu', '/pl/unso', '/ledger', '/cf/matrix', '/budget', '/pl/balances'] as const;

export function appOfPath(pathname: string): AppTarget {
  const p = pathname.replace(/\/+$/, '') || '/';
  return (BOARD_PATHS as readonly string[]).includes(p) ? 'board' : 'gate';
}

function cleanUrl(raw: string | undefined): string | null {
  const s = (raw ?? '').trim().replace(/\/+$/, '');
  return /^https?:\/\/[^/]+/.test(s) ? s : null;
}

/** 相手のアプリの URL(設定が無ければ null) */
export function appBaseUrl(target: AppTarget, env: { VITE_GATE_URL?: string; VITE_BOARD_URL?: string }): string | null {
  return cleanUrl(target === 'gate' ? env.VITE_GATE_URL : env.VITE_BOARD_URL);
}

/**
 * この画面をいまのアプリで出すか。出さないときは相手のアプリへの案内を出す
 *  - Gate で Board の画面: Board の URL が設定済みなら案内、未設定なら今までどおり出す(移行中)
 *  - Board で Gate の画面: 必ず案内(Board に入力の画面は置かない)
 */
export function shouldRenderHere(app: AppTarget, pathname: string, boardUrl: string | null): boolean {
  const owner = appOfPath(pathname);
  if (owner === app) return true;
  if (app === 'gate' && owner === 'board') return boardUrl === null;
  return false;
}

/** 画面の入室条件(src/App.tsx と同じ条件。変えるときは両方そろえる) */
export type BoardAccess = {
  companyPl: boolean;
  confirm: boolean;
  admin: boolean;
  /** 部門長で、部門長を務める事業部がある */
  buchoWithDepts: boolean;
  /** 運送の部門長(運送PL を見られる・2026-10-23) */
  buchoUnso?: boolean;
  /** 事業部予実の権限があり、本部に所属 */
  deptPlInHonbu: boolean;
};

/** 利用者の権限から BoardAccess を組む(App.tsx と AppLayout.tsx で同じものを使う) */
export function boardAccessOf(a: {
  can: (perm: 'company_pl' | 'department_pl' | 'master_admin') => boolean;
  canConfirm: boolean;
  flowRole: string;
  user: { department_ids: string[]; manager_department_ids: string[] } | null | undefined;
}): BoardAccess {
  return {
    companyPl: a.can('company_pl'),
    confirm: a.canConfirm,
    admin: a.can('master_admin'),
    buchoWithDepts: a.flowRole === 'bucho' && (a.user?.manager_department_ids.length ?? 0) > 0,
    buchoUnso: a.flowRole === 'bucho' && (a.user?.manager_department_ids.includes('unso') ?? false),
    deptPlInHonbu: a.can('department_pl') && (a.user?.department_ids.includes('honbu') ?? false),
  };
}

export type BoardMenuItem = { to: (typeof BOARD_PATHS)[number]; label: string; visible: boolean };

export function boardMenu(a: BoardAccess): BoardMenuItem[] {
  const seesHonbu = a.companyPl || a.confirm || a.deptPlInHonbu;
  return [
    { to: '/pl', label: '全社PL', visible: a.companyPl },
    { to: '/pl/accounts', label: '勘定科目別 予実', visible: a.companyPl },
    { to: '/pl/dept', label: '事業部予実', visible: a.buchoWithDepts },
    { to: '/pl/honbu', label: '本部PL', visible: seesHonbu },
    // 運送PL(2026-10-23 発注者指示。SC Board_v2「運送」シートの形)。全社PL を見られる人・経理/承認者・運送の部門長
    { to: '/pl/unso', label: '運送PL', visible: seesUnso(a) },
    { to: '/ledger', label: '経費管理表', visible: a.confirm || a.admin },
    { to: '/cf/matrix', label: 'CF月次推移', visible: a.confirm },
    // 売掛・買掛・未払残高(B4)。経理・管理者・承認者(「確定」の権限)。メニューに出す(2026-10-23 発注者決定⑦)
    { to: '/pl/balances', label: '売掛・買掛・未払残高', visible: a.confirm },
    { to: '/budget', label: '予算入力', visible: a.companyPl && a.admin },
  ];
}

/** 運送PL を見られる人(src/App.tsx の入室条件と同じ。権限ルールの routine_pl の読み取りもこれに合わせる) */
export function seesUnso(a: BoardAccess): boolean {
  return a.companyPl || a.confirm || a.buchoUnso === true;
}

/** Board でログインしたあとに最初に開く画面。見られる画面が無ければ null */
export function boardHomePath(a: BoardAccess): string | null {
  return boardMenu(a).find((i) => i.visible)?.to ?? null;
}

/** Board を使える人か(Board の画面を1つでも見られる人) */
export function canUseBoard(a: BoardAccess): boolean {
  return boardHomePath(a) !== null;
}
